/**
 * MonitorDetailsDialog - Full-screen dialog for viewing/editing monitor details
 *
 * Displays complete monitor information including configuration, statistics, and scan history.
 * Supports actions like triggering scans, toggling status, editing, and deletion.
 */

import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Edit,
  Loader2,
  AlertTriangle,
  Play,
  Pause,
  Trash2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useMonitor,
  useMonitorStats,
  useMonitorScans,
  useTriggerScan,
  useToggleMonitorStatus,
  useDeleteMonitor,
} from '@/features/monitors/hooks/useMonitors';
import { MonitorStatusBadge } from './MonitorStatusBadge';
import { MonitorTypeBadge } from './MonitorTypeBadge';
import { MonitorStatsCard } from './MonitorStatsCard';
import { useToast } from '@/hooks/useToast';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import type { MonitorScan } from '@/features/monitors/types/monitor.types';
import { formatDuration } from '@/utils/formatDuration';

interface MonitorDetailsDialogProps {
  /**
   * Monitor ID to display (null to close dialog)
   */
  monitorId: string | null;
  /**
   * Callback when dialog is closed
   */
  onClose: () => void;
  /**
   * Optional callback to trigger edit mode
   */
  onEdit?: (monitorId: string) => void;
}


/**
 * MonitorDetailsDialog Component
 *
 * Full-screen dialog showing detailed monitor information with tabs for
 * details, statistics, and scan history.
 *
 * @example
 * ```tsx
 * const [selectedId, setSelectedId] = useState<string | null>(null);
 *
 * <MonitorDetailsDialog
 *   monitorId={selectedId}
 *   onClose={() => setSelectedId(null)}
 *   onEdit={(id) => navigate(`/monitors/${id}/edit`)}
 * />
 * ```
 */
export const MonitorDetailsDialog: React.FC<MonitorDetailsDialogProps> = ({
  monitorId,
  onClose,
  onEdit,
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch data
  const { data: monitor, isLoading, error } = useMonitor(monitorId || '', { enabled: !!monitorId });
  const { data: stats, isLoading: statsLoading } = useMonitorStats(monitorId || '', { enabled: !!monitorId });
  const { data: scans, isLoading: scansLoading } = useMonitorScans(monitorId || '', { limit: 50 }, { enabled: !!monitorId });

  // Mutations
  const triggerScan = useTriggerScan();
  const toggleStatus = useToggleMonitorStatus();
  const deleteMonitor = useDeleteMonitor();

  /**
   * Handle trigger scan
   */
  const handleTriggerScan = async () => {
    if (!monitor) return;

    try {
      const response = await triggerScan.mutateAsync({
        monitor_id: monitor.id,
        force: true,
      });
      toast({
        title: 'Scan Triggered',
        description: `Scan initiated successfully. Scan ID: ${response.scan_id}`,
      });
    } catch (error: any) {
      console.error('Failed to trigger scan:', error);
      toast({
        title: 'Scan Failed',
        description: error.response?.data?.detail || 'Failed to trigger scan. Monitor may be paused.',
        variant: 'destructive',
      });
    }
  };

  /**
   * Handle toggle status
   */
  const handleToggleStatus = async () => {
    if (!monitor) return;

    const newStatus = monitor.status === 'active' ? 'paused' : 'active';

    try {
      await toggleStatus.mutateAsync({
        monitor_id: monitor.id,
        status: newStatus,
      });
      toast({
        title: 'Status Updated',
        description: `Monitor ${newStatus === 'active' ? 'resumed' : 'paused'} successfully`,
      });
    } catch (error: any) {
      console.error('Failed to toggle status:', error);
      toast({
        title: 'Update Failed',
        description: error.response?.data?.detail || 'Failed to update monitor status',
        variant: 'destructive',
      });
    }
  };

  /**
   * Handle delete monitor
   */
  const handleDelete = async () => {
    if (!monitor) return;

    try {
      await deleteMonitor.mutateAsync(monitor.id);
      toast({
        title: 'Monitor Deleted',
        description: `Monitor "${monitor.name}" has been deleted`,
      });
      onClose();
    } catch (error: any) {
      console.error('Failed to delete monitor:', error);
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete monitor. It may have active scans.',
        variant: 'destructive',
      });
    }
  };

  /**
   * AG Grid column definitions for scan history
   */
  const scanColumns = useMemo<ColumnDef<MonitorScan>[]>(
    () => [
      {
        accessorKey: 'started_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Started At</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.started_at;
          if (!value) return '';
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm:ss');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'duration',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Duration</DataTableSortHeader>
        ),
        cell: ({ row }) => formatDuration(row.original.duration, { emptyText: 'N/A', zeroIsEmpty: true }),
        sortingFn: (a, b) => (a.original.duration ?? 0) - (b.original.duration ?? 0),
      },
      {
        accessorKey: 'files_discovered',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files Discovered</DataTableSortHeader>
        ),
      },
      {
        accessorKey: 'files_skipped',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files Skipped</DataTableSortHeader>
        ),
      },
      {
        accessorKey: 'jobs_created',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Jobs Created</DataTableSortHeader>
        ),
      },
      {
        accessorKey: 'success',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Status</DataTableSortHeader>
        ),
        cell: ({ row }) =>
          row.original.success ? (
            <Badge variant="outline" className="bg-success/10 text-success border-success">
              <CheckCircle className="h-3 w-3 mr-1" />
              Success
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-destructive/10 text-destructive border-destructive"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Failed
            </Badge>
          ),
      },
      {
        accessorKey: 'error_message',
        header: 'Error Message',
        cell: ({ row }) => {
          const value = row.original.error_message;
          if (!value) return '—';
          return (
            <span className="text-destructive truncate" title={value}>
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  if (!monitorId) return null;

  return (
    <>
      <Dialog open={!!monitorId} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="space-y-2">
              <DialogTitle className="text-2xl flex items-center gap-2">
                {monitor?.name || 'Loading...'}
                {monitor && <MonitorStatusBadge status={monitor.status} />}
              </DialogTitle>
              <DialogDescription>
                {monitor?.description || 'Monitor details, statistics, and scan history'}
              </DialogDescription>
            </div>
          </DialogHeader>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Failed to load monitor: {error.message || 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {/* Monitor Details */}
          {monitor && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Action Buttons */}
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  onClick={handleTriggerScan}
                  disabled={triggerScan.isPending || monitor.status !== 'active'}
                >
                  {triggerScan.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Triggering...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Trigger Scan
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleToggleStatus}
                  disabled={toggleStatus.isPending}
                >
                  {toggleStatus.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : monitor.status === 'active' ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Resume
                    </>
                  )}
                </Button>
                {onEdit && (
                  <Button size="sm" variant="outline" onClick={() => onEdit(monitor.id)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteMonitor.isPending}
                  className="ml-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="statistics">Statistics</TabsTrigger>
                  <TabsTrigger value="scan-history">Scan History</TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-y-auto mt-4">
                  {/* Details Tab */}
                  <TabsContent value="details" className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Monitor Configuration</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Monitor Type</p>
                            <MonitorTypeBadge type={monitor.monitor_type} />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Scan Frequency</p>
                            <p className="font-medium">{monitor.scan_frequency}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Auto Ingest</p>
                            <Badge variant={monitor.auto_ingest ? 'default' : 'secondary'}>
                              {monitor.auto_ingest ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Directory Path</p>
                            <p className="font-medium font-mono text-sm">{monitor.directory_path}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Target Dataset</p>
                            <p className="font-medium">{monitor.dataset?.name || 'None'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Default Schema</p>
                            <p className="font-medium">{monitor.schema?.name || 'None'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>File Patterns</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Include Patterns</p>
                          <div className="flex flex-wrap gap-2">
                            {monitor.file_patterns.include_patterns.map((pattern, index) => (
                              <Badge key={index} variant="secondary">
                                {pattern}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {monitor.file_patterns.exclude_patterns.length > 0 && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-2">Exclude Patterns</p>
                            <div className="flex flex-wrap gap-2">
                              {monitor.file_patterns.exclude_patterns.map((pattern, index) => (
                                <Badge key={index} variant="outline">
                                  {pattern}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-muted-foreground">Recursive</p>
                          <Badge variant={monitor.file_patterns.recursive ? 'default' : 'secondary'}>
                            {monitor.file_patterns.recursive ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Schedule & Timing</CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Last Scan</p>
                          <p className="font-medium">
                            {monitor.last_scan_at
                              ? format(new Date(monitor.last_scan_at), 'MMM d, yyyy HH:mm')
                              : 'Never'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Next Scan</p>
                          <p className="font-medium">
                            {monitor.next_scan_at
                              ? format(new Date(monitor.next_scan_at), 'MMM d, yyyy HH:mm')
                              : 'Not scheduled'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Trigger Delay</p>
                          <p className="font-medium">{monitor.trigger_delay_seconds || 5}s</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Batch Size</p>
                          <p className="font-medium">{monitor.batch_size || 10} files</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Metadata</CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Created</p>
                          <p className="font-medium">
                            {format(new Date(monitor.created_at), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Last Updated</p>
                          <p className="font-medium">
                            {format(new Date(monitor.updated_at), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Statistics Tab */}
                  <TabsContent value="statistics">
                    <MonitorStatsCard stats={stats} isLoading={statsLoading} />
                  </TabsContent>

                  {/* Scan History Tab */}
                  <TabsContent value="scan-history">
                    <Card>
                      <CardHeader>
                        <CardTitle>Scan History</CardTitle>
                        <CardDescription>Recent directory scan executions</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {scansLoading && (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          </div>
                        )}

                        {!scansLoading && (!scans || scans.length === 0) && (
                          <div className="text-center py-8 text-muted-foreground">
                            No scan history available yet.
                          </div>
                        )}

                        {!scansLoading && scans && scans.length > 0 && (
                          <DataTable
                            data={scans}
                            columns={scanColumns}
                            pageSize={20}
                            initialSorting={[{ id: 'started_at', desc: true }]}
                            getRowId={(s) => s.scan_id}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Monitor?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete monitor "{monitor?.name}"? This will stop monitoring the
              directory and remove all scan history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMonitor.isPending}
            >
              {deleteMonitor.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Monitor'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
